// src/resources/POSMappings/posAssignmentUtils.js

import { db } from "../../firebase/firebase";
import {
  doc,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  setDoc,
  getDoc,
} from "firebase/firestore";

const pad2 = (n) => String(n).padStart(2, "0");

// Convert input (Date | Timestamp | "YYYY-MM-DD") -> "YYYY-MM-DD"
const toDateOnlyString = (v) => {
  if (!v) return null;

  // Firestore Timestamp
  if (typeof v?.toDate === "function") v = v.toDate();

  // JS Date
  if (v instanceof Date) {
    return `${v.getFullYear()}-${pad2(v.getMonth() + 1)}-${pad2(v.getDate())}`;
  }

  // "YYYY-MM-DD"
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

  // fallback
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const parseDateOnlyLocal = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d); // local midnight
};

const addDays = (dateStr, days) => {
  const d = parseDateOnlyLocal(dateStr);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/* ------------------------------------------------------------------
   Find mapping document by terminalId
------------------------------------------------------------------- */
export async function findMappingDocByTerminalId(terminalId) {
  const terminalKey = String(terminalId).trim();

  const q = query(
    collection(db, "posDeviceMappings"),
    where("terminalId", "==", terminalKey)
  );

  const snap = await getDocs(q);
  if (snap.empty) return null;

  return { id: snap.docs[0].id, data: snap.docs[0].data() };
}

/* ------------------------------------------------------------------
   Close active assignment (NEW STRUCTURE)
------------------------------------------------------------------- */
export async function closeActiveAssignment(terminalId, endDate) {
  const terminalKey = String(terminalId).trim();
  const endDateStr = toDateOnlyString(endDate);
  if (!endDateStr) return;

  const assignmentRef = doc(db, "posDeviceAssignments", terminalKey);
  const snap = await getDoc(assignmentRef);

  if (!snap.exists()) return;

  const data = snap.data() || {};
  const assignments = Array.isArray(data.assignments) ? data.assignments : [];

  const updatedAssignments = [...assignments];
  const activeIndex = updatedAssignments.findIndex((a) => !a?.endDate);

  if (activeIndex >= 0) {
    updatedAssignments[activeIndex] = {
      ...updatedAssignments[activeIndex],
      endDate: endDateStr,
      updatedAt: new Date(),
    };

    await updateDoc(assignmentRef, {
      assignments: updatedAssignments,
      currentVehicleId: "",
      updatedAt: new Date(),
    });
  }
}

/* ------------------------------------------------------------------
   Create new assignment (NEW STRUCTURE)
   - stores startDate/endDate as DATE-ONLY strings "YYYY-MM-DD"
   - auto-closes previous active assignment (endDate = day before)
------------------------------------------------------------------- */
export async function createNewAssignment(terminalId, vehicleId, chosenDate) {
  if (!vehicleId) throw new Error("Missing vehicleId");
  if (!chosenDate) throw new Error("Missing chosenDate");

  const terminalKey = String(terminalId).trim();

  const startDateStr = toDateOnlyString(chosenDate);
  if (!startDateStr) throw new Error("Invalid chosenDate");

  const assignmentRef = doc(db, "posDeviceAssignments", terminalKey);
  const snap = await getDoc(assignmentRef);

  let updatedAssignments = [];

  if (snap.exists()) {
    const existing = snap.data()?.assignments || [];

    updatedAssignments = existing.map((a) => {
      // Close previous active assignment safely
      if (!a?.endDate) {
        const prevStart = toDateOnlyString(a.startDate);
        const newEnd = addDays(startDateStr, -1);

        // Safety: never allow endDate to be before the previous startDate
        const safeEnd = prevStart && newEnd < prevStart ? prevStart : newEnd;

        return {
          ...a,
          endDate: safeEnd,
          updatedAt: new Date(),
        };
      }
      return a;
    });
  }

  updatedAssignments.push({
    vehicleId,
    startDate: startDateStr, // save as "YYYY-MM-DD"
    endDate: null,           // will become "YYYY-MM-DD" when closed
    createdAt: new Date(),
  });

  await setDoc(
    assignmentRef,
    {
      terminalId: terminalKey,
      currentVehicleId: vehicleId,
      assignments: updatedAssignments,
      updatedAt: new Date(),
      ...(snap.exists() ? {} : { createdAt: new Date() }),
    },
    { merge: true }
  );

  await updateOldMapping(terminalKey, vehicleId);
}

/* ------------------------------------------------------------------
   Update old mapping structure (backwards compatibility)
------------------------------------------------------------------- */
async function updateOldMapping(terminalId, vehicleId) {
  const terminalKey = String(terminalId).trim();
  const mapping = await findMappingDocByTerminalId(terminalKey);

  // Keep the cached vehicleNumber synchronized for legacy screens/exports,
  // while vehicleId remains the authoritative mapping value.
  let vehicleNumber = "";
  try {
    const vehicleSnap = await getDoc(doc(db, "vehicles", vehicleId));
    if (vehicleSnap.exists()) {
      vehicleNumber = String(vehicleSnap.data()?.vehicleNumber || "").trim();
    }
  } catch (error) {
    // Do not block a reassignment if only the display-name lookup fails.
    console.warn("Could not refresh POS vehicleNumber cache:", error);
  }

  if (mapping) {
    await updateDoc(doc(db, "posDeviceMappings", mapping.id), {
      vehicleId,
      vehicleNumber,
      updatedAt: new Date(),
    });
  } else {
    await addDoc(collection(db, "posDeviceMappings"), {
      terminalId: terminalKey,
      vehicleId,
      vehicleNumber,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  }
}
